import React, { useEffect, useState } from "react";
import { X, Plus, Music2, Globe, Lock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export const AddToPlaylistModal = ({ open, onClose, trackId }) => {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    api.get("/playlists", { params: { user_id: user.user_id, public_only: false } })
      .then(({ data }) => setPlaylists(data)).catch(() => {});
  }, [open, user]);

  if (!open) return null;

  if (!user) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="add-playlist-modal">
        <div className="bg-brand-surface border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
          <p className="text-white/70 mb-4">Connectez-vous pour créer des playlists</p>
          <a href="/login" className="btn-primary inline-block">Se connecter</a>
        </div>
      </div>
    );
  }

  const createAndAdd = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/playlists", { title: newTitle, is_public: true });
      if (trackId) {
        await api.post(`/playlists/${data.playlist_id}/tracks`, { track_id: trackId });
      }
      toast.success("Playlist créée");
      setNewTitle("");
      onClose();
    } catch {
      toast.error("Erreur");
    } finally { setCreating(false); }
  };

  const addTo = async (pl) => {
    try {
      await api.post(`/playlists/${pl.playlist_id}/tracks`, { track_id: trackId });
      toast.success(`Ajouté à "${pl.title}"`);
      onClose();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="add-playlist-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-md bg-brand-surface border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-black inline-flex items-center gap-2"><Music2 size={18} /> Ajouter à une playlist</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <form onSubmit={createAndAdd} className="flex gap-2 mb-5">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Nom d'une nouvelle playlist..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 outline-none focus:border-brand-red/50 text-sm"
            data-testid="new-playlist-input"
          />
          <button disabled={creating || !newTitle.trim()} className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40" data-testid="create-playlist-btn">
            <Plus size={14} /> Créer
          </button>
        </form>

        {playlists.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-4">Pas encore de playlist. Créez-en une !</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {playlists.map((pl) => {
              const included = pl.track_ids?.includes(trackId);
              return (
                <button
                  key={pl.playlist_id}
                  onClick={() => !included && addTo(pl)}
                  disabled={included}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 text-left transition-colors disabled:opacity-50"
                  data-testid={`add-to-playlist-${pl.playlist_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center">
                    {pl.is_public ? <Globe size={14} /> : <Lock size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{pl.title}</p>
                    <p className="text-[11px] text-white/50">{pl.track_ids?.length || 0} morceaux</p>
                  </div>
                  {included && <span className="text-xs text-brand-red font-medium">Déjà inclus</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToPlaylistModal;
